/*
  PDF-Erzeugung für den Schichtplan V2 (jsPDF, A4).

  Zeichnet ein Zeilen-x-Tage-Raster aus dem Export-Modell – immer auf genau
  eine Seite. Passt der Plan nicht, werden Zeilen, Einträge und Schriftgrade
  gestaucht statt umgebrochen: ein Schichtplan ist ein Aushang, und über zwei
  Seiten verteilt kann man ihn nicht mehr am Stück lesen. Hat die Seite Platz,
  wachsen sie bis zu einer Grenze mit, statt unten ein Drittel leer zu lassen.

  Wird ein Eintrag dabei zu flach für Zeit und Name untereinander, rücken
  beide in eine Zeile – der Zellinhalt bleibt vollständig, nur enger gesetzt.

  Ferien und Absenzen stehen wie im Raster als ein schraffierter Block über
  alle ihre Tage. Die Schichten bleiben vollflächig und sind damit das, was
  man auf dem Aushang zuerst sieht.

  Dieselbe Datei speist die Vorschau im Dialog (`createShiftplanPdfObjectUrl`),
  damit dort wirklich das steht, was heruntergeladen wird. Das Seitenlayout
  (`buildShiftplanPdfLayout`) rechnet ohne jsPDF, damit auch der Dialog daraus
  lesen kann.
*/

import { EXPORT_ENTRY_KINDS } from "../../../domain/model/exportModel";
import { buildEntryPalette, buildTimeOffPalette, hexToRgb } from "../exportColors";

// A4 in Punkt – dieselbe Seite, die jsPDF unten aufzieht.
const A4_SHORT_SIDE = 595.28;
const A4_LONG_SIDE = 841.89;
const PAGE_MARGIN = 22;
const HEADER_HEIGHT = 46;
const FOOTER_HEIGHT = 30;
// Kopf der Tagesspalten: knapp mit Tageszahl und Kürzel (Monat), höher mit
// ausgeschriebenem Wochentag (Woche) – und eine Zeile mehr für Feiertagsnamen.
const DAY_HEADER_HEIGHT = 24;
const WIDE_DAY_HEADER_HEIGHT = 30;
const HOLIDAY_NAME_LINE_HEIGHT = 8;
// Ab dieser Spaltenbreite passt "Donnerstag" ausgeschrieben in den Kopf.
const LONG_WEEKDAY_MIN_COLUMN_WIDTH = 52;
const NAME_COLUMN_WIDTH = 96;
const GRID_CORNER_RADIUS = 4;
const ENTRY_CORNER_RADIUS = 1.4;
const MIN_ROW_HEIGHT = 20;
// So weit wachsen die Zeilen höchstens, wenn die Seite Platz hat: darüber
// würden die Einträge zu Kacheln, und die Schrift wächst ohnehin nicht mehr mit.
const MAX_ROW_GROWTH = 2.2;
// Untergrenze beim Stauchen: darunter ist ein Eintrag kein Balken mehr.
const MIN_ENTRY_HEIGHT = 3.4;
// Seitlicher Abstand des Eintrags zu den Linien seiner Tagesspalte.
const CELL_PADDING = 1.6;
// Luft über dem ersten und unter dem letzten Eintrag einer Zeile sowie
// zwischen zwei Einträgen am selben Tag. Beides schrumpft beim Stauchen mit.
const ROW_INSET = 2.8;
const ENTRY_GAP = 1;
// Ab dieser Höhe stehen Zeit und Name lesbar untereinander (beide über 4pt).
const TWO_LINE_MIN_ENTRY_HEIGHT = 13;
// Abstand zwischen Zeit und Name, wenn beide in einer Zeile stehen.
const SINGLE_LINE_GAP_EM = 0.5;
// Abstand zwischen Zeit und Name, wenn sie untereinander stehen.
const TWO_LINE_GAP_EM = 0.42;
// Grösste Grade, auf die die Schrift mit der Zeile wächst.
const MAX_CODE_FONT_SIZE = 7;
const MAX_ENTRY_FONT_SIZE = 9;
const MAX_NAME_FONT_SIZE = 9.4;
// "00:00 - 00:00" in Helvetica fett, in em. Die Zeit wird nie gekürzt – ihr
// Grad richtet sich darum auch nach der Spaltenbreite (Woche im Hochformat).
const TIME_LABEL_WIDTH_EM = 6.1;
// Höhe der Grossbuchstaben in Helvetica, in em – zum Zentrieren von Text.
const CAP_HEIGHT_EM = 0.72;
// Kleinster Grad, der auf dem Aushang noch lesbar ist – darunter warnt der Dialog.
const MIN_LEGIBLE_FONT_SIZE = 4;
const LEGEND_GAP = 10;
// Etwas breiter als der reine Name: die Chips tragen jetzt auch das Kürzel.
const LEGEND_CHIP_WIDTH = 146;
const TEXT_OVERFLOW_MARKER = "..";
// Bei ganztägigen Ferien und Absenzen sagt die Zeit nichts – dort steht nur der Name.
const ALL_DAY_TIME_LABEL = "Ganztag";
// Ferien und Absenzen sind ein Stück niedriger als die Schichten: ein Band
// im Hintergrund der Zeile, kein weiterer Eintrag. Anteil oben und unten.
const TIME_OFF_BAND_INSET = 0.12;
const TIMETAB_LOGO_URL = `${process.env.PUBLIC_URL || ""}/assets/TimeTab_Logo_quer_cmyk_pos.png`;

// Ferien und Absenzen: im Raster wie hier ein Block über alle ihre Tage.
const TIME_OFF_KINDS = new Set([EXPORT_ENTRY_KINDS.ABSENCE, EXPORT_ENTRY_KINDS.HOLIDAY]);

/*
  Farben des Rasters. Wochenende und Feiertag tönen ihre ganze Spalte, damit
  man sie auf dem Ausdruck auch mitten im Plan erkennt; Feiertage in Indigo
  wie im Raster (--spv2-public-holiday). Die Linien laufen über alle Spalten
  durch und sind kräftiger als jede Tönung – sonst verschwinden sie im Grau
  des Wochenendes.
*/
const GRID_COLORS = Object.freeze({
  headerFill: "#F1F3F7",
  weekendHeaderFill: "#E3E8EF",
  holidayHeaderFill: "#E0E6FF",
  weekendColumnFill: "#F0F3F7",
  holidayColumnFill: "#EFF1FF",
  zebraFill: "#FAFBFD",
  line: "#D2D9E2",
  strongLine: "#B7C1CE",
  frame: "#0F172A",
  holidayText: "#4F46E5",
});
const GRID_LINE_WIDTH = 0.4;
const STRONG_LINE_WIDTH = 0.5;

/*
  Was in einer Tageszelle steht – ein Format für das ganze Blatt:
  - Kürzel: die Monatsspalte ist zu schmal für Text, die Legende löst auf.
  - Zweizeilig: Zeit fett, darunter Abteilung · Schicht.
  - Einzeilig: zu flach für zwei Zeilen – der Name rückt neben die Zeit.
*/
const CELL_TEXT_MODES = Object.freeze({
  CODE: "code",
  TWO_LINE: "two-line",
  SINGLE_LINE: "single-line",
});

const CREATED_LABEL_FORMATTER = new Intl.DateTimeFormat("de-CH", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

function cleanText(value) {
  return String(value || "").trim();
}

function setPdfFillColor(pdf, color) {
  const { r, g, b } = hexToRgb(color);
  pdf.setFillColor(r, g, b);
}

function setPdfDrawColor(pdf, color) {
  const { r, g, b } = hexToRgb(color);
  pdf.setDrawColor(r, g, b);
}

function setPdfTextColor(pdf, color) {
  const { r, g, b } = hexToRgb(color);
  pdf.setTextColor(r, g, b);
}

function fitPdfText(pdf, text, maxWidth) {
  const source = cleanText(text);
  if (!source) return "";
  if (pdf.getTextWidth(source) <= maxWidth) return source;

  let output = source;
  while (output.length > 1 && pdf.getTextWidth(`${output}${TEXT_OVERFLOW_MARKER}`) > maxWidth) {
    output = output.slice(0, -1);
  }
  return output.length > 1 ? `${output}${TEXT_OVERFLOW_MARKER}` : "";
}

function drawPdfText(pdf, text, x, y, maxWidth, options = {}) {
  const fitted = fitPdfText(pdf, text, maxWidth);
  if (!fitted) return;
  pdf.text(fitted, x, y, options);
}

const logoDataPromiseByUrl = new Map();

function loadImageData(url) {
  if (logoDataPromiseByUrl.has(url)) return logoDataPromiseByUrl.get(url);

  const promise = new Promise((resolve) => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      resolve(null);
      return;
    }

    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = image.naturalWidth || image.width;
        canvas.height = image.naturalHeight || image.height;
        const context = canvas.getContext("2d");
        if (!context || !canvas.width || !canvas.height) {
          resolve(null);
          return;
        }
        context.drawImage(image, 0, 0);
        resolve({ dataUrl: canvas.toDataURL("image/png"), width: canvas.width, height: canvas.height });
      } catch (_error) {
        resolve(null);
      }
    };
    image.onerror = () => resolve(null);
    image.src = url;
  });

  logoDataPromiseByUrl.set(url, promise);
  return promise;
}

// Höhe eines Eintrags, solange nichts gestaucht werden muss.
export function getEntryRowLayout(dayColumnWidth) {
  // Bei schmalen Spalten (Monatsansicht) reicht der Platz nur für das Kürzel.
  const isCompact = dayColumnWidth < 34;
  return {
    isCompact,
    entryHeight: isCompact ? 10 : 15.5,
  };
}

// Breite, die der Text eines Eintrags in seiner Tagesspalte hat (neben dem Farbstreifen).
function getEntryTextWidth(dayColumnWidth) {
  return dayColumnWidth - CELL_PADDING * 2 - 5.5;
}

function clampFontSize(value, minimum, maximum) {
  return Math.max(minimum, Math.min(maximum, value));
}

/*
  Schriftgrade eines Eintrags. Sie hängen an seiner Höhe, weil die auf einer
  vollen Seite gestaucht wird – ein fester Grad würde aus seinem Kasten laufen.
  Mit Text steht auch die Spaltenbreite im Spiel: die Zeit muss ungekürzt
  hineinpassen. `secondary` ist der kleinere der beiden; im Kürzel und in der
  einzeiligen Zelle sind sie gleich.
*/
function getEntryFontSizes(textMode, entryHeight, textWidth = Infinity) {
  if (textMode === CELL_TEXT_MODES.CODE) {
    const size = clampFontSize(entryHeight * 0.54, 3, MAX_CODE_FONT_SIZE);
    return { primary: size, secondary: size };
  }

  const widthLimit = textWidth / TIME_LABEL_WIDTH_EM;

  if (textMode === CELL_TEXT_MODES.TWO_LINE) {
    const primary = clampFontSize(Math.min(entryHeight * 0.36, widthLimit), 3, MAX_ENTRY_FONT_SIZE);
    return { primary, secondary: primary * 0.9 };
  }

  // Eine Zeile hat den ganzen Kasten für sich – der Grad darf grösser sein.
  const size = clampFontSize(Math.min(entryHeight * 0.62, widthLimit), 3, MAX_ENTRY_FONT_SIZE);
  return { primary: size, secondary: size };
}

// Der Name steht einzeilig in der ganzen Zeilenhöhe.
function getNameFontSize(rowHeight) {
  return clampFontSize(rowHeight * 0.48, 3.4, MAX_NAME_FONT_SIZE);
}

/*
  Wo in einer Zeile was steht. Ferien und Absenzen werden zu Blöcken über
  aufeinanderfolgende Tage zusammengefasst und liegen oben, jeder auf einer
  eigenen Spur (`lane`), die er über alle seine Tage behält. Die Schichten
  eines Tages folgen unter der untersten Spur, die diesen Tag belegt – wie
  im Raster, wo Ferienbalken über den Schichten liegen.

  `slotCount` ist die Zahl der Einträge, die die Zeile untereinander tragen
  muss: daran misst sich ihre Höhe.
*/
export function buildRowSlotLayout(row, dayKeys) {
  const timeOffBlocks = [];
  const openBlockByKey = new Map();
  const shiftsByDayIndex = dayKeys.map(() => []);

  dayKeys.forEach((dayKey, dayIndex) => {
    (row.cellsByDayKey[dayKey] || []).forEach((entry) => {
      if (!TIME_OFF_KINDS.has(entry.kind)) {
        shiftsByDayIndex[dayIndex].push(entry);
        return;
      }

      const blockKey = `${entry.kind}:${entry.entryId}`;
      const openBlock = openBlockByKey.get(blockKey);
      if (openBlock && openBlock.endIndex === dayIndex - 1) {
        openBlock.endIndex = dayIndex;
        return;
      }

      const block = { entry, startIndex: dayIndex, endIndex: dayIndex, lane: 0 };
      timeOffBlocks.push(block);
      openBlockByKey.set(blockKey, block);
    });
  });

  // Spuren wie im Raster: der früheste Block zuerst, bei gleichem Start der längere.
  const laneEnds = [];
  [...timeOffBlocks]
    .sort((left, right) => left.startIndex - right.startIndex || right.endIndex - left.endIndex)
    .forEach((block) => {
      const freeLane = laneEnds.findIndex((endIndex) => endIndex < block.startIndex);
      block.lane = freeLane === -1 ? laneEnds.length : freeLane;
      laneEnds[block.lane] = block.endIndex;
    });

  const shiftSlotStartByDayIndex = dayKeys.map((_dayKey, dayIndex) =>
    timeOffBlocks.reduce(
      (count, block) =>
        block.startIndex <= dayIndex && dayIndex <= block.endIndex ? Math.max(count, block.lane + 1) : count,
      0
    )
  );
  const slotCount = shiftsByDayIndex.reduce(
    (maximum, shifts, dayIndex) => Math.max(maximum, shiftSlotStartByDayIndex[dayIndex] + shifts.length),
    1
  );

  return { timeOffBlocks, shiftsByDayIndex, shiftSlotStartByDayIndex, slotCount };
}

/*
  Höhe jeder Zeile für die eine Seite.

  Zuerst die Wunschhöhe (Einträge x Eintragshöhe plus Luft), dann alle Zeilen
  mit demselben Faktor auf die Seite bringen: gestaucht, wenn die Summe die
  Seite sprengt, gestreckt (höchstens um MAX_ROW_GROWTH), wenn Platz bleibt –
  ein Aushang mit wenigen Leuten soll das Blatt füllen und dafür grösser
  geschrieben sein. Der gemeinsame Faktor ist wichtig: skalierte man jede
  Zeile einzeln, verlören Zeilen mit zwei Einträgen ihr Verhältnis zu den
  anderen.

  Die Luft skaliert mit. Fest gelassen, nahm sie bei 30 Mitarbeitenden ein
  Drittel der Zeile – Platz, der den Einträgen und ihrer Schrift fehlte. Die
  Einträge füllen die Zeile mit gleicher Luft oben und unten.
*/
export function buildSinglePageRowLayout(rows, dayKeys, { entryHeight, availableHeight }) {
  const slotLayouts = rows.map((row) => buildRowSlotLayout(row, dayKeys));
  const naturalHeights = slotLayouts.map(({ slotCount }) =>
    Math.max(MIN_ROW_HEIGHT, slotCount * entryHeight + (slotCount - 1) * ENTRY_GAP + ROW_INSET * 2)
  );
  const naturalTotal = naturalHeights.reduce((sum, height) => sum + height, 0);
  const scale = naturalTotal > 0 ? Math.min(MAX_ROW_GROWTH, availableHeight / naturalTotal) : 1;
  const entryInset = ROW_INSET * scale;
  const entryGap = ENTRY_GAP * scale;

  return rows.map((row, index) => {
    const slots = slotLayouts[index];
    const count = slots.slotCount;
    const rowHeight = naturalHeights[index] * scale;
    const stackHeight = rowHeight - entryInset * 2 - entryGap * (count - 1);

    return {
      row,
      rowHeight,
      entryCount: count,
      entryHeight: Math.max(MIN_ENTRY_HEIGHT, stackHeight / count),
      entryInset,
      entryGap,
      slots,
    };
  });
}

function getSmallestEntryHeight(rowLayouts, fallback) {
  return rowLayouts.length ? Math.min(...rowLayouts.map((item) => item.entryHeight)) : fallback;
}

function estimateLegendHeight(legend, gridWidth) {
  if (!legend.length) return 0;
  const chipsPerLine = Math.max(1, Math.floor(gridWidth / (LEGEND_CHIP_WIDTH + 6)));
  return 16 + Math.ceil(legend.length / chipsPerLine) * 17;
}

function hasPublicHoliday(day) {
  return Array.isArray(day.publicHolidayNames) && day.publicHolidayNames.length > 0;
}

/*
  Das Layout der einen Seite, ohne zu zeichnen. Der Dialog liest daraus, ob die
  Zelle nur das Kürzel trägt (dann sperrt er die wirkungslosen
  Zellinhalt-Schalter) und ob die Schrift noch lesbar bleibt.

  Das Zellformat gilt für das ganze Blatt und richtet sich nach dem flachsten
  Eintrag: stünde die Zeile mit zwei Einsätzen einzeilig und der Rest
  zweizeilig, läse sich der Plan uneinheitlich.
*/
export function buildShiftplanPdfLayout({ model, settings }) {
  const orientation = settings.orientation === "portrait" ? "portrait" : "landscape";
  const pageWidth = orientation === "portrait" ? A4_SHORT_SIDE : A4_LONG_SIDE;
  const pageHeight = orientation === "portrait" ? A4_LONG_SIDE : A4_SHORT_SIDE;
  const gridX = PAGE_MARGIN;
  const gridY = PAGE_MARGIN + HEADER_HEIGHT;
  const gridWidth = pageWidth - PAGE_MARGIN * 2;
  const dayColumnWidth = (gridWidth - NAME_COLUMN_WIDTH) / Math.max(1, model.days.length);
  const { isCompact, entryHeight } = getEntryRowLayout(dayColumnWidth);
  const usesLongWeekdays = dayColumnWidth >= LONG_WEEKDAY_MIN_COLUMN_WIDTH;
  const showsHolidayNames = usesLongWeekdays && model.days.some(hasPublicHoliday);
  let dayHeaderHeight = DAY_HEADER_HEIGHT;
  if (usesLongWeekdays) {
    dayHeaderHeight = WIDE_DAY_HEADER_HEIGHT + (showsHolidayNames ? HOLIDAY_NAME_LINE_HEIGHT : 0);
  }
  const legend = settings.includeLegend ? model.legend : [];
  const legendHeight = legend.length ? estimateLegendHeight(legend, gridWidth) + LEGEND_GAP : 0;
  const availableRowHeight = pageHeight - gridY - PAGE_MARGIN - FOOTER_HEIGHT - dayHeaderHeight - legendHeight;
  const rowLayouts = buildSinglePageRowLayout(
    model.rows,
    model.days.map((day) => day.key),
    { entryHeight, availableHeight: availableRowHeight }
  );

  const smallestEntryHeight = getSmallestEntryHeight(rowLayouts, entryHeight);
  const smallestRowHeight = rowLayouts.length ? Math.min(...rowLayouts.map((item) => item.rowHeight)) : MIN_ROW_HEIGHT;
  let cellTextMode = CELL_TEXT_MODES.SINGLE_LINE;
  if (isCompact) {
    cellTextMode = CELL_TEXT_MODES.CODE;
  } else if (smallestEntryHeight >= TWO_LINE_MIN_ENTRY_HEIGHT) {
    cellTextMode = CELL_TEXT_MODES.TWO_LINE;
  }
  const nameFontSize = getNameFontSize(smallestRowHeight);
  const entryTextWidth = getEntryTextWidth(dayColumnWidth);

  /*
    Lesbar heisst: die regulären Zeilen – ein Eintrag pro Tag – bleiben über
    dem kleinsten lesbaren Grad. Eine Zelle mit zwei Einsätzen ist im Monat
    immer enger als die übrigen; sie allein soll den Hinweis nicht auslösen, er
    gilt der Zahl der Zeilen. Gemessen wird nur, was mit den Zeilen schrumpft –
    Kopf, Legende und Fuss haben feste Grade.
  */
  const regularRows = rowLayouts.filter((item) => item.entryCount === 1);
  const regularEntryHeight = getSmallestEntryHeight(regularRows.length ? regularRows : rowLayouts, entryHeight);
  const smallestFontSize = Math.min(
    nameFontSize,
    getEntryFontSizes(cellTextMode, regularEntryHeight, entryTextWidth).secondary
  );

  return {
    orientation,
    pageWidth,
    pageHeight,
    gridX,
    gridY,
    gridWidth,
    dayColumnWidth,
    dayHeaderHeight,
    usesLongWeekdays,
    showsHolidayNames,
    isCompact,
    cellTextMode,
    nameFontSize,
    entryTextWidth,
    legend,
    rowLayouts,
    isTextLegible: smallestFontSize >= MIN_LEGIBLE_FONT_SIZE,
  };
}

function drawPageHeader(pdf, { title, subtitle, periodLabel, companyName, pageWidth }) {
  const kicker = "SCHICHTPLAN";
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9.5);
  setPdfTextColor(pdf, "#64748B");
  pdf.text(kicker, PAGE_MARGIN, PAGE_MARGIN + 8);

  // Der Betrieb gleich daneben: ein Aushang ohne Absender lässt offen, wessen Plan das ist.
  const company = cleanText(companyName);
  if (company) {
    const companyX = PAGE_MARGIN + pdf.getTextWidth(kicker) + 9.5 * 0.5;
    setPdfTextColor(pdf, "#0F172A");
    drawPdfText(pdf, company, companyX, PAGE_MARGIN + 8, pageWidth * 0.62 - (companyX - PAGE_MARGIN));
  }

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(21);
  setPdfTextColor(pdf, "#030213");
  drawPdfText(pdf, title || periodLabel, PAGE_MARGIN, PAGE_MARGIN + 30, pageWidth * 0.62);

  const secondaryText = [cleanText(subtitle), title && periodLabel && title !== periodLabel ? periodLabel : ""]
    .filter(Boolean)
    .join(" · ");
  if (secondaryText) {
    pdf.setFontSize(11);
    setPdfTextColor(pdf, "#475569");
    drawPdfText(
      pdf,
      secondaryText,
      pageWidth - PAGE_MARGIN,
      PAGE_MARGIN + 30,
      pageWidth * 0.32,
      { align: "right" }
    );
  }
}

// Feiertag vor Wochenende: fällt einer auf einen Samstag, zählt der Feiertag.
function getDayColumnFills(day) {
  if (hasPublicHoliday(day)) {
    return { header: GRID_COLORS.holidayHeaderFill, body: GRID_COLORS.holidayColumnFill };
  }
  if (day.isWeekend) {
    return { header: GRID_COLORS.weekendHeaderFill, body: GRID_COLORS.weekendColumnFill };
  }
  return null;
}

// "21.12" aus dem Raster, im Aushang mit dem Schlusspunkt des Datums.
function formatDayDate(day) {
  const label = cleanText(day.dayMonthLabel);
  return label && !label.endsWith(".") ? `${label}.` : label;
}

/*
  Beschriftung eines Tageskopfs; die Tönung liegt da schon (siehe
  getDayColumnFills). In der Woche steht der Wochentag ausgeschrieben über
  dem Datum, ein Feiertag zusätzlich mit Namen. Im Monat reicht die Spalte
  nur für Tageszahl und Kürzel – dort zeigt sich der Feiertag an Farbe und Punkt.
*/
function drawDayHeader(pdf, { day, x, y, width, height, usesLongWeekdays, showsHolidayNames }) {
  const isHoliday = hasPublicHoliday(day);
  const centerX = x + width / 2;
  const primaryColor = isHoliday ? GRID_COLORS.holidayText : "#0F172A";
  const secondaryColor = isHoliday ? GRID_COLORS.holidayText : "#64748B";
  const weekdayShort = cleanText(day.weekdayShort).replace(".", "");

  if (usesLongWeekdays) {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8.2);
    setPdfTextColor(pdf, primaryColor);
    drawPdfText(pdf, cleanText(day.weekdayLong) || weekdayShort, centerX, y + 12.4, width - 4, { align: "center" });

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7);
    setPdfTextColor(pdf, secondaryColor);
    drawPdfText(pdf, formatDayDate(day), centerX, y + 22, width - 4, { align: "center" });

    if (showsHolidayNames && isHoliday) {
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(6.2);
      drawPdfText(pdf, day.publicHolidayNames.join(", "), centerX, y + 30.6, width - 6, { align: "center" });
    }
    return;
  }

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(Math.min(7, width * 0.26));
  setPdfTextColor(pdf, primaryColor);
  pdf.text(String(day.dayNumber), centerX, y + height * 0.46, { align: "center" });

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(Math.min(5.4, width * 0.22));
  setPdfTextColor(pdf, secondaryColor);
  drawPdfText(pdf, weekdayShort, centerX, y + height * 0.82, width - 2, { align: "center" });

  if (isHoliday) {
    setPdfFillColor(pdf, GRID_COLORS.holidayText);
    pdf.circle(centerX, y + height - 1.6, 1, "F");
  }
}

/*
  Grund und Schraffur von Ferien und Absenzen, wie im Raster: die Streifen
  laufen von links unten nach rechts oben und werden auf die runde Form
  beschnitten. Ihr Abstand wächst mit der Höhe, damit die Schraffur auch in
  der gestauchten Monatszeile noch als Muster erkennbar bleibt.
*/
function drawTimeOffShape(pdf, { x, y, width, height, radius, palette }) {
  const stripeSpacing = Math.max(2.6, Math.min(4.6, height * 0.3));

  pdf.saveGraphicsState();
  pdf.roundedRect(x, y, width, height, radius, radius, null);
  pdf.clip();
  pdf.discardPath();

  setPdfFillColor(pdf, palette.body);
  pdf.rect(x, y, width, height, "F");

  setPdfDrawColor(pdf, palette.stripe);
  pdf.setLineWidth(stripeSpacing * 0.36);
  for (let startX = x - height; startX < x + width; startX += stripeSpacing * Math.SQRT2) {
    pdf.line(startX, y + height, startX + height, y);
  }
  pdf.restoreGraphicsState();

  setPdfDrawColor(pdf, palette.border);
  pdf.setLineWidth(0.25);
  pdf.roundedRect(x, y, width, height, radius, radius, "S");
}

/*
  Ferien oder Absenz als ein Block über alle ihre Tage. Er soll zurücktreten:
  kein Farbstreifen wie bei den Schichten, etwas niedriger als ein Eintrag,
  die Schrift gedämpft und nicht fett. Die Beschriftung steht einmal in der
  Mitte: der Name, bei einer Absenz mit Uhrzeit auch die Zeit. Passt das
  nicht, bleibt der Name – im Monat zuletzt das Kürzel.
*/
function drawTimeOffBlock(pdf, { entry, x, y, width, height, textMode, fontSize, monochrome }) {
  const palette = buildTimeOffPalette(entry.color, { monochrome });
  const isCode = textMode === CELL_TEXT_MODES.CODE;
  const innerX = x + CELL_PADDING;
  const innerWidth = width - CELL_PADDING * 2;
  const innerHeight = Math.max(MIN_ENTRY_HEIGHT, height);
  const bandInset = innerHeight * TIME_OFF_BAND_INSET;

  drawTimeOffShape(pdf, {
    x: innerX,
    y: y + bandInset,
    width: innerWidth,
    height: innerHeight - bandInset * 2,
    radius: ENTRY_CORNER_RADIUS,
    palette,
  });

  const textLeft = innerX + 1.5;
  const textRight = innerX + innerWidth - 1.5;
  const textWidth = textRight - textLeft;

  const name = cleanText(entry.name) || cleanText(entry.fullLabel);
  const time = cleanText(entry.time) === ALL_DAY_TIME_LABEL ? "" : cleanText(entry.time);
  const candidates = [time && name ? `${name} · ${time}` : "", name, isCode ? cleanText(entry.code) : ""].filter(
    Boolean
  );
  if (!candidates.length) return;

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(fontSize);
  setPdfTextColor(pdf, palette.text);
  const label =
    candidates.find((text) => pdf.getTextWidth(text) <= textWidth) ||
    fitPdfText(pdf, candidates[candidates.length - 1], textWidth);
  if (!label) return;
  pdf.text(label, (textLeft + textRight) / 2, y + innerHeight / 2 + fontSize * 0.35, { align: "center" });
}

/*
  Ein Eintrag in der Tageszelle, im Zellformat des ganzen Blatts (siehe
  CELL_TEXT_MODES). Wird es einzeilig, bleibt die Zeit vollständig und nur der
  Name wird gekürzt: wann jemand arbeitet, ist die Auskunft, die zählt – der
  Name steckt zudem in Farbe und Legende.
*/
function drawCellEntry(pdf, { entry, x, y, width, height, textMode, fontSizes, monochrome }) {
  const palette = buildEntryPalette(entry.color, { monochrome });
  const innerX = x + CELL_PADDING;
  const innerWidth = width - CELL_PADDING * 2;
  const innerHeight = Math.max(MIN_ENTRY_HEIGHT, height);

  setPdfFillColor(pdf, palette.body);
  setPdfDrawColor(pdf, palette.border);
  pdf.setLineWidth(0.3);
  if (entry.isDraft) pdf.setLineDashPattern([1.1, 0.9], 0);
  pdf.roundedRect(innerX, y, innerWidth, innerHeight, ENTRY_CORNER_RADIUS, ENTRY_CORNER_RADIUS, "FD");
  if (entry.isDraft) pdf.setLineDashPattern([], 0);

  if (textMode === CELL_TEXT_MODES.CODE) {
    // Zeit und Name wurden hier zu ".." abgeschnitten – in der schmalen Spalte
    // steht darum nur das Kürzel; wofür es steht, sagt die Legende.
    const compactText = cleanText(entry.code) || cleanText(entry.name) || cleanText(entry.fullLabel);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(fontSizes.primary);
    setPdfTextColor(pdf, palette.text);
    drawPdfText(
      pdf,
      compactText,
      innerX + innerWidth / 2,
      y + innerHeight / 2 + fontSizes.primary * 0.35,
      innerWidth - 1.4,
      { align: "center" }
    );
    return;
  }

  setPdfFillColor(pdf, palette.accent);
  pdf.roundedRect(innerX + 1.1, y + 1.1, 1.1, Math.max(1.2, innerHeight - 2.2), 0.5, 0.5, "F");

  const textX = innerX + 4;
  const textWidth = innerWidth - 5.5;
  // Ohne Zeit steht der Name allein und führt.
  const primaryText = cleanText(entry.time) || cleanText(entry.name) || cleanText(entry.fullLabel);
  const secondaryText = entry.time ? cleanText(entry.name) : "";
  const centeredBaseline = y + innerHeight / 2 + fontSizes.primary * 0.35;

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(fontSizes.primary);
  setPdfTextColor(pdf, palette.text);

  if (!secondaryText) {
    drawPdfText(pdf, primaryText, textX, centeredBaseline, textWidth);
    return;
  }

  if (textMode === CELL_TEXT_MODES.TWO_LINE) {
    // Zeit und Name als Block in der Mitte des Eintrags: wächst die Zeile,
    // bleiben sie beieinander, statt an Ober- und Unterkante zu wandern.
    const lineGap = fontSizes.primary * TWO_LINE_GAP_EM;
    const blockHeight = (fontSizes.primary + fontSizes.secondary) * CAP_HEIGHT_EM + lineGap;
    const primaryBaseline = y + (innerHeight - blockHeight) / 2 + fontSizes.primary * CAP_HEIGHT_EM;
    drawPdfText(pdf, primaryText, textX, primaryBaseline, textWidth);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(fontSizes.secondary);
    drawPdfText(
      pdf,
      secondaryText,
      textX,
      primaryBaseline + lineGap + fontSizes.secondary * CAP_HEIGHT_EM,
      textWidth
    );
    return;
  }

  const fittedPrimary = fitPdfText(pdf, primaryText, textWidth);
  if (!fittedPrimary) return;
  pdf.text(fittedPrimary, textX, centeredBaseline);

  // Gemessen, solange noch die fette Schrift der Zeit gesetzt ist.
  const secondaryX = textX + pdf.getTextWidth(fittedPrimary) + fontSizes.primary * SINGLE_LINE_GAP_EM;
  pdf.setFont("helvetica", "normal");
  drawPdfText(pdf, secondaryText, secondaryX, centeredBaseline, textX + textWidth - secondaryX);
}

function drawGridPage(pdf, { model, settings, layout }) {
  const {
    gridX,
    gridY,
    gridWidth,
    dayColumnWidth,
    dayHeaderHeight,
    usesLongWeekdays,
    showsHolidayNames,
    cellTextMode,
    nameFontSize,
    entryTextWidth,
    rowLayouts,
  } = layout;
  const days = model.days;
  const daysX = gridX + NAME_COLUMN_WIDTH;
  const bodyY = gridY + dayHeaderHeight;
  const bodyHeight = rowLayouts.reduce((sum, item) => sum + item.rowHeight, 0);
  const gridHeight = dayHeaderHeight + bodyHeight;
  const monochrome = settings.colorMode === "mono";
  const getDayX = (dayIndex) => daysX + dayIndex * dayColumnWidth;

  /*
    Alles im Rahmen wird auf dessen runde Ecken beschnitten. So braucht der
    Kopf keine eigene Rundung, und eine getönte Sonntagsspalte steht nicht
    eckig über die Ecke hinaus.
  */
  pdf.saveGraphicsState();
  pdf.roundedRect(gridX, gridY, gridWidth, gridHeight, GRID_CORNER_RADIUS, GRID_CORNER_RADIUS, null);
  pdf.clip();
  pdf.discardPath();

  // Flächen: Kopf, jede zweite Zeile, darüber Wochenend- und Feiertagsspalten.
  setPdfFillColor(pdf, GRID_COLORS.headerFill);
  pdf.rect(gridX, gridY, gridWidth, dayHeaderHeight, "F");

  let rowTop = bodyY;
  rowLayouts.forEach(({ rowHeight }, rowIndex) => {
    if (rowIndex % 2 === 1) {
      setPdfFillColor(pdf, GRID_COLORS.zebraFill);
      pdf.rect(gridX, rowTop, gridWidth, rowHeight, "F");
    }
    rowTop += rowHeight;
  });

  days.forEach((day, dayIndex) => {
    const fills = getDayColumnFills(day);
    if (!fills) return;
    setPdfFillColor(pdf, fills.header);
    pdf.rect(getDayX(dayIndex), gridY, dayColumnWidth, dayHeaderHeight, "F");
    setPdfFillColor(pdf, fills.body);
    pdf.rect(getDayX(dayIndex), bodyY, dayColumnWidth, bodyHeight, "F");
  });

  /*
    Linien vor den Einträgen: so laufen sie durch die getönten Spalten
    hindurch, und ein Ferienblock liegt als Ganzes über den Tagesgrenzen,
    die er überspannt – wie im Raster. Zuvor kamen die Zeilenlinien nach der
    Zeile und die nächste Wochenendfläche deckte sie halb zu.
  */
  setPdfDrawColor(pdf, GRID_COLORS.line);
  pdf.setLineWidth(GRID_LINE_WIDTH);
  rowTop = bodyY;
  rowLayouts.forEach(({ rowHeight }) => {
    rowTop += rowHeight;
    pdf.line(gridX, rowTop, gridX + gridWidth, rowTop);
  });
  days.forEach((_day, dayIndex) => {
    if (dayIndex === 0) return;
    pdf.line(getDayX(dayIndex), gridY, getDayX(dayIndex), gridY + gridHeight);
  });
  setPdfDrawColor(pdf, GRID_COLORS.strongLine);
  pdf.setLineWidth(STRONG_LINE_WIDTH);
  pdf.line(daysX, gridY, daysX, gridY + gridHeight);
  pdf.line(gridX, bodyY, gridX + gridWidth, bodyY);

  // Neben ausgeschriebenen Wochentagen eine Stufe grösser, sonst wirkt es verloren.
  const cornerFontSize = usesLongWeekdays ? 7.4 : 6.4;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(cornerFontSize);
  setPdfTextColor(pdf, "#334155");
  pdf.text("Mitarbeitende", gridX + 5, gridY + dayHeaderHeight / 2 + cornerFontSize * 0.35);

  days.forEach((day, dayIndex) => {
    drawDayHeader(pdf, {
      day,
      x: getDayX(dayIndex),
      y: gridY,
      width: dayColumnWidth,
      height: dayHeaderHeight,
      usesLongWeekdays,
      showsHolidayNames,
    });
  });

  // Zeilen: Name, darüber liegende Ferien/Absenzen, darunter die Schichten.
  rowTop = bodyY;
  rowLayouts.forEach(({ row, rowHeight, entryHeight, entryInset, entryGap, slots }) => {
    const slotTop = rowTop + entryInset;
    const getSlotY = (slotIndex) => slotTop + slotIndex * (entryHeight + entryGap);
    const fontSizes = getEntryFontSizes(cellTextMode, entryHeight, entryTextWidth);

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(nameFontSize);
    setPdfTextColor(pdf, "#0F172A");
    drawPdfText(pdf, row.name, gridX + 5, rowTop + rowHeight / 2 + nameFontSize * 0.35, NAME_COLUMN_WIDTH - 9);

    slots.timeOffBlocks.forEach((block) => {
      drawTimeOffBlock(pdf, {
        entry: block.entry,
        x: getDayX(block.startIndex),
        y: getSlotY(block.lane),
        width: (block.endIndex - block.startIndex + 1) * dayColumnWidth,
        height: entryHeight,
        textMode: cellTextMode,
        // Eine Stufe leiser als die Zeit der Schichten.
        fontSize: fontSizes.secondary,
        monochrome,
      });
    });

    slots.shiftsByDayIndex.forEach((entries, dayIndex) => {
      entries.forEach((entry, entryIndex) => {
        drawCellEntry(pdf, {
          entry,
          x: getDayX(dayIndex),
          y: getSlotY(slots.shiftSlotStartByDayIndex[dayIndex] + entryIndex),
          width: dayColumnWidth,
          height: entryHeight,
          textMode: cellTextMode,
          fontSizes,
          monochrome,
        });
      });
    });

    rowTop += rowHeight;
  });

  pdf.restoreGraphicsState();

  setPdfDrawColor(pdf, GRID_COLORS.frame);
  pdf.setLineWidth(0.55);
  pdf.roundedRect(gridX, gridY, gridWidth, gridHeight, GRID_CORNER_RADIUS, GRID_CORNER_RADIUS, "S");

  return gridY + gridHeight;
}

function drawLegend(pdf, { legend, x, y, width, monochrome }) {
  if (!legend.length) return y;

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(7);
  setPdfTextColor(pdf, "#334155");
  pdf.text("Legende", x, y + 6);

  const chipWidth = LEGEND_CHIP_WIDTH;
  const chipHeight = 13;
  const chipsPerLine = Math.max(1, Math.floor(width / (chipWidth + 6)));

  legend.forEach((item, index) => {
    const chipX = x + (index % chipsPerLine) * (chipWidth + 6);
    const chipY = y + 10 + Math.floor(index / chipsPerLine) * (chipHeight + 4);
    const isTimeOff = TIME_OFF_KINDS.has(item.kind);
    const palette = isTimeOff
      ? buildTimeOffPalette(item.color, { monochrome })
      : buildEntryPalette(item.color, { monochrome });

    if (isTimeOff) {
      // Schraffiert und ohne Farbstreifen wie im Plan: die Legende erklärt auch das Muster.
      drawTimeOffShape(pdf, { x: chipX, y: chipY, width: chipWidth, height: chipHeight, radius: 2, palette });
    } else {
      setPdfFillColor(pdf, palette.body);
      setPdfDrawColor(pdf, palette.border);
      pdf.setLineWidth(0.3);
      pdf.roundedRect(chipX, chipY, chipWidth, chipHeight, 2, 2, "FD");
      setPdfFillColor(pdf, palette.accent);
      pdf.roundedRect(chipX + 2, chipY + 2, 1.4, chipHeight - 4, 0.6, 0.6, "F");
    }

    const labelBaseline = chipY + chipHeight / 2 + 2;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(5.8);
    setPdfTextColor(pdf, palette.text);
    // Das Kürzel voran: im Monatsraster ist es alles, was in der Zelle steht.
    const name = item.timeLabel ? `${item.name} · ${item.timeLabel}` : item.name;
    const label = [item.code, name].filter(Boolean).join(" · ");
    drawPdfText(pdf, label, chipX + 6.5, labelBaseline, chipWidth - 26);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(5.4);
    drawPdfText(pdf, `${item.count}x`, chipX + chipWidth - 4, labelBaseline, 18, { align: "right" });
  });

  const lineCount = Math.ceil(legend.length / chipsPerLine);
  return y + 10 + lineCount * (chipHeight + 4);
}

function drawPageFooter(pdf, { pageWidth, pageHeight, footerText, hasDrafts, logoData }) {
  const lineY = pageHeight - PAGE_MARGIN - 16;

  setPdfDrawColor(pdf, "#CBD5E1");
  pdf.setLineWidth(0.4);
  pdf.line(PAGE_MARGIN, lineY, pageWidth - PAGE_MARGIN, lineY);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(6.6);
  setPdfTextColor(pdf, "#64748B");

  const footerParts = [cleanText(footerText), hasDrafts ? "Gestrichelt umrandet = Entwurf (nicht freigegeben)" : ""]
    .filter(Boolean)
    .join("  ·  ");
  drawPdfText(pdf, footerParts, PAGE_MARGIN, lineY + 10, pageWidth * 0.52);

  // Keine Seitenzahl: der Plan steht immer auf genau einer Seite.
  const rightLabel = `Erstellt am ${CREATED_LABEL_FORMATTER.format(new Date())}`;

  if (logoData?.dataUrl) {
    const logoHeight = 9;
    const logoWidth = logoData.width && logoData.height ? (logoHeight * logoData.width) / logoData.height : 30;
    const logoX = pageWidth - PAGE_MARGIN - logoWidth;
    try {
      pdf.addImage(logoData.dataUrl, "PNG", logoX, lineY + 3.5, logoWidth, logoHeight);
      pdf.text(rightLabel, logoX - 6, lineY + 10, { align: "right" });
      return;
    } catch (_error) {
      // Textvariante unten, falls das Logo nicht eingebettet werden kann.
    }
  }

  pdf.text([rightLabel, "TimeTab"].filter(Boolean).join("  ·  "), pageWidth - PAGE_MARGIN, lineY + 10, {
    align: "right",
  });
}

export function buildPdfFilename(periodLabel) {
  const periodPart = cleanText(periodLabel)
    .replace(/[\\/:*?"<>|·]+/g, " ")
    .replace(/\s+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 48);
  return `Schichtplan_${periodPart || "Export"}.pdf`;
}

async function createShiftplanPdf({ model, settings, periodLabel, companyName = "" }) {
  const { jsPDF } = await import("jspdf");
  const logoData = await loadImageData(TIMETAB_LOGO_URL);

  const layout = buildShiftplanPdfLayout({ model, settings });
  const { pageWidth, pageHeight } = layout;
  const pdf = new jsPDF({ orientation: layout.orientation, unit: "pt", format: "a4", compress: true });

  pdf.setFillColor(255, 255, 255);
  pdf.rect(0, 0, pageWidth, pageHeight, "F");

  drawPageHeader(pdf, {
    title: settings.title,
    subtitle: settings.subtitle,
    periodLabel,
    companyName,
    pageWidth,
  });

  const gridBottom = drawGridPage(pdf, { model, settings, layout });

  if (layout.legend.length) {
    drawLegend(pdf, {
      legend: layout.legend,
      x: layout.gridX,
      y: gridBottom + LEGEND_GAP,
      width: layout.gridWidth,
      monochrome: settings.colorMode === "mono",
    });
  }

  drawPageFooter(pdf, {
    pageWidth,
    pageHeight,
    footerText: settings.footerText,
    hasDrafts: model.stats.draftCount > 0,
    logoData,
  });

  return pdf;
}

export async function downloadShiftplanPdf({ model, settings, periodLabel, companyName }) {
  const pdf = await createShiftplanPdf({ model, settings, periodLabel, companyName });
  pdf.save(buildPdfFilename(periodLabel));
}

/*
  Dieselbe Datei als Objekt-URL für die Vorschau im Dialog. Die Vorschau zeigt
  damit nicht einen Nachbau, sondern das Dokument selbst – wer sie aufruft, muss
  die URL nach Gebrauch mit URL.revokeObjectURL wieder freigeben.
*/
export async function createShiftplanPdfObjectUrl({ model, settings, periodLabel, companyName }) {
  const pdf = await createShiftplanPdf({ model, settings, periodLabel, companyName });
  return URL.createObjectURL(pdf.output("blob"));
}
